// An agent's recent runs with how each ended (ADR 0039 §4), from the host's runs port (the host's
// ledger). The Runs tab of every agent, a platform persona included.
import { cn } from '@cortex/ui';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { agentRunsKey, useAgentsHost, useAgentsQuery } from './host.tsx';
import { failureText, outcomeText, outcomeTone, TONE_CLASS } from './outcome.ts';
import { messageOf } from './ui.tsx';

export function OutcomeBadge({
  outcome,
  stop,
  testId = 'run-outcome',
}: {
  outcome: string;
  stop?: { budget?: string; check?: string } | null;
  testId?: string;
}) {
  const failure = failureText(outcome, stop);
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span
        data-testid={testId}
        data-outcome={outcome}
        className={cn(
          'inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap',
          TONE_CLASS[outcomeTone(outcome)],
        )}
      >
        {outcomeText(outcome, stop)}
      </span>
      {failure ? (
        <span className="text-[11px] text-page-muted whitespace-nowrap" data-testid={`${testId}-failure`}>
          {failure}
        </span>
      ) : null}
    </span>
  );
}

export function RecentRuns({ persona }: { persona: string }) {
  const host = useAgentsHost();
  const q = useAgentsQuery(
    agentRunsKey(host.cache.runs, persona, 'recent'),
    async () => {
      try {
        return { runs: await host.runs!({ persona, all: true, limit: 20 }), error: '' };
      } catch (e) {
        return { runs: [], error: messageOf(e) };
      }
    },
    { enabled: !!host.runs },
  );
  if (!host.runs) return null;
  if (q.loading && !q.data)
    return (
      <div className="flex items-center gap-2 text-xs text-page-muted">
        <Loader2 size={12} className="animate-spin" /> Loading runs…
      </div>
    );
  const runs = q.data?.runs ?? [];
  if (q.data?.error) return <div className="text-xs text-red-600">{q.data.error}</div>;
  if (!runs.length) return <div className="text-xs text-page-muted">No runs you can see yet.</div>;
  return (
    <ul className="m-0 p-0 list-none divide-y divide-divider" data-testid="agent-recent-runs">
      {runs.map((r) => (
        <li
          key={r.id}
          className="py-1.5 text-xs flex items-center gap-2 min-w-0"
          data-testid="agent-recent-run"
        >
          {r.outcome ? (
            <OutcomeBadge outcome={r.outcome} stop={r.stop ?? null} />
          ) : (
            <span className="text-page-muted">{r.status}</span>
          )}
          <span className="text-page-muted whitespace-nowrap">{new Date(r.startedAt).toLocaleString()}</span>
          <span className="truncate text-page-text">{r.task.slice(0, 140)}</span>
          {r.harness ? (
            <span className="ml-auto font-mono text-page-muted" title="The harness version the run ran on">
              {r.harness.hash.slice(0, 8)}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
